import React, { useCallback, useEffect, useState } from 'react'
import { App } from '@capacitor/app'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Radio,
  Typography,
} from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { Capacitor } from '@capacitor/core'
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings'

import LabelPrinter, {
  IBluetoothPrinterDevice,
  IPrinterIdentity,
  IUsbPrinterDevice,
  isLabelPrinterAvailable,
} from '../services/print/labelPrinter'
import {
  getPreferredBluetoothName,
  getPreferredUsbDeviceKey,
  getPrinterModel,
  setPreferredBluetoothName,
  setPreferredUsbDeviceKey,
  setPrinterDpi,
  setPrinterModel,
} from '../services/printer/printerSettings'

// The dialog renders on the app's dark palette, where MUI's defaults for secondary
// text resolve to near-black. Pinned explicitly for the same reason the organization
// dialog does it — see useOrganizationSwitchDialog.
const PRIMARY_TEXT_COLOR = '#e6e7e9'
const SECONDARY_TEXT_COLOR = '#a0a0a0'

/**
 * Lets the seller choose which label printer to print to — a paired Bluetooth printer
 * by name, or a printer on the USB cable. USB access is granted per device by an
 * Android system dialog, so this is also where that consent is collected, ahead of a
 * print rather than in the middle of one.
 */
export function usePrinterSelectDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [bluetoothDevices, setBluetoothDevices] = useState<IBluetoothPrinterDevice[]>([])
  const [usbDevices, setUsbDevices] = useState<IUsbPrinterDevice[]>([])
  const [bluetoothPermissionGranted, setBluetoothPermissionGranted] = useState(false)
  const [selectedBluetoothName, setSelectedBluetoothName] = useState<string | undefined>(getPreferredBluetoothName)
  const [selectedUsbDeviceKey, setSelectedUsbDeviceKey] = useState<string | undefined>(getPreferredUsbDeviceKey)
  const [identity, setIdentity] = useState<IPrinterIdentity | undefined>()
  const [identifyError, setIdentifyError] = useState<string | undefined>()
  const [isIdentifying, setIsIdentifying] = useState(false)

  const refresh = useCallback(async () => {
    if (!isLabelPrinterAvailable()) {
      return
    }

    setIsLoading(true)
    try {
      const status = await LabelPrinter.getStatus()
      setBluetoothPermissionGranted(status.bluetoothPermissionGranted)

      // Listing bonded devices needs BLUETOOTH_CONNECT; skip rather than letting the
      // plugin reject, so the dialog can offer the permission button instead.
      if (status.bluetoothPermissionGranted) {
        const { devices } = await LabelPrinter.listBluetoothDevices()
        setBluetoothDevices(devices)
      } else {
        setBluetoothDevices([])
      }

      if (status.usbSupported) {
        const { devices } = await LabelPrinter.listUsbDevices()
        setUsbDevices(devices)
      } else {
        setUsbDevices([])
      }
    } catch (error) {
      console.error('[usePrinterSelectDialog] failed to enumerate printers', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Pairing a printer and granting USB access both happen outside this dialog — in
   * Android's Bluetooth settings, or in the system dialog raised by plugging the cable
   * in. Neither notifies us, so without this the list still shows the pre-change state
   * when the seller comes back and it looks as though the pairing did not take.
   */
  useEffect(() => {
    if (!isOpen || !Capacitor.isNativePlatform()) {
      return
    }

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        refresh()
      }
    })

    return () => {
      listener.then(handle => handle.remove()).catch(() => undefined)
    }
  }, [isOpen, refresh])

  const open = () => {
    setSelectedBluetoothName(getPreferredBluetoothName())
    setSelectedUsbDeviceKey(getPreferredUsbDeviceKey())
    setIsOpen(true)
    refresh()
  }

  const handleClose = () => setIsOpen(false)

  const handleRequestBluetoothPermission = async () => {
    try {
      await LabelPrinter.requestPermissions()
    } catch (error) {
      console.error('[usePrinterSelectDialog] Bluetooth permission request failed', error)
    }
    // Refresh either way: the seller may have granted it from the system dialog even
    // if the call itself reported an error.
    await refresh()
  }

  const handleSelectBluetooth = (device: IBluetoothPrinterDevice) => {
    setPreferredBluetoothName(device.name)
    setSelectedBluetoothName(device.name)
  }

  const handleGrantUsb = async (device: IUsbPrinterDevice) => {
    // Only an explicit false is unusable: an older APK's plugin predates this field
    // and omits it, and refusing to grant there would break USB on tablets that
    // already work.
    if (device.supported === false) {
      return
    }

    // Recorded before the grant, so choosing a printer sticks even if the seller
    // dismisses the system permission dialog and grants it later.
    setPreferredUsbDeviceKey(device.deviceKey)
    setSelectedUsbDeviceKey(device.deviceKey)

    if (device.hasPermission) {
      return
    }

    try {
      await LabelPrinter.requestUsbPermission({ deviceKey: device.deviceKey })
    } catch (error) {
      console.error('[usePrinterSelectDialog] USB permission request failed', error)
    }
    await refresh()
  }

  /**
   * Asks the printer its model and resolution over ZPL ~HI and stores the dpi.
   *
   * Only stores what the printer actually reported: a printer that answers but whose
   * reply cannot be parsed leaves the previous dpi alone rather than overwriting it
   * with a guess, because a wrong dpi rescales every label without any visible error.
   */
  const handleIdentify = async (transport: 'usb' | 'bluetooth') => {
    setIsIdentifying(true)
    setIdentifyError(undefined)

    try {
      const result = await LabelPrinter.identifyPrinter({
        transport,
        ...(selectedUsbDeviceKey ? { deviceKey: selectedUsbDeviceKey } : {}),
        ...(selectedBluetoothName ? { deviceName: selectedBluetoothName } : {}),
      })

      setIdentity(result)

      if (result.dpi) {
        setPrinterDpi(result.dpi)
      }
      if (result.model) {
        setPrinterModel(result.model)
      }
    } catch (error) {
      console.error('[usePrinterSelectDialog] identify failed', error)
      setIdentifyError((error as { message?: string })?.message ?? 'Could not identify the printer')
    } finally {
      setIsIdentifying(false)
    }
  }

  const handleOpenBluetoothSettings = () => {
    NativeSettings.open({
      optionAndroid: AndroidSettings.Bluetooth,
      optionIOS: IOSSettings.Bluetooth,
    }).catch(error => console.error('[usePrinterSelectDialog] could not open Bluetooth settings', error))
  }

  const dialog = (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth='xs'
      PaperProps={{ sx: { backgroundColor: '#1e1e1e', backgroundImage: 'none', color: PRIMARY_TEXT_COLOR } }}
    >
      <DialogTitle>
        <FormattedMessage
          id='app.components.app-drawer.printer.dialog-title'
          defaultMessage='Choose printer'
        />
      </DialogTitle>

      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.8rem', textTransform: 'uppercase' }}>
          <FormattedMessage
            id='app.components.app-drawer.printer.bluetooth'
            defaultMessage='Bluetooth'
          />
        </Typography>

        {!bluetoothPermissionGranted && (
          <Button onClick={handleRequestBluetoothPermission} sx={{ mt: 1 }}>
            <FormattedMessage
              id='app.components.app-drawer.printer.allow-bluetooth'
              defaultMessage='Allow Bluetooth access'
            />
          </Button>
        )}

        {bluetoothPermissionGranted && bluetoothDevices.length === 0 && !isLoading && (
          <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.85rem', mt: 1 }}>
            <FormattedMessage
              id='app.components.app-drawer.printer.no-paired'
              defaultMessage='No paired printers. Pair one in the Android Bluetooth settings first.'
            />
          </Typography>
        )}

        <List dense>
          {bluetoothDevices.map(device => (
            <ListItemButton key={device.address} onClick={() => handleSelectBluetooth(device)}>
              <Radio
                checked={device.name === selectedBluetoothName}
                size='small'
                sx={{ color: SECONDARY_TEXT_COLOR }}
              />
              <ListItemText
                primary={device.name}
                secondary={device.address}
                primaryTypographyProps={{ sx: { color: PRIMARY_TEXT_COLOR } }}
                secondaryTypographyProps={{ sx: { color: SECONDARY_TEXT_COLOR } }}
              />
            </ListItemButton>
          ))}
        </List>

        <Button onClick={handleOpenBluetoothSettings} size='small'>
          <FormattedMessage
            id='app.components.app-drawer.printer.pair-new'
            defaultMessage='Pair a new printer'
          />
        </Button>

        <Divider sx={{ my: 2, borderColor: '#3a3a3a' }} />

        <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.8rem', textTransform: 'uppercase' }}>
          <FormattedMessage
            id='app.components.app-drawer.printer.usb'
            defaultMessage='USB cable'
          />
        </Typography>

        {usbDevices.length === 0 && !isLoading && (
          <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.85rem', mt: 1 }}>
            <FormattedMessage
              id='app.components.app-drawer.printer.no-usb'
              defaultMessage='No printer detected on the cable.'
            />
          </Typography>
        )}

        <List dense>
          {usbDevices.map(device => (
            <ListItemButton
              key={device.deviceId}
              onClick={() => handleGrantUsb(device)}
              // Usable rows stay tappable even once permitted, since tapping now also
              // selects which printer to use; only unusable devices are inert.
              disabled={device.supported === false}
            >
              {/* Mirrors the Bluetooth list: without a visible selection the seller
                  cannot tell which cable printer a tap actually chose. */}
              <Radio
                checked={device.deviceKey === selectedUsbDeviceKey}
                disabled={device.supported === false}
                size='small'
                sx={{ color: SECONDARY_TEXT_COLOR }}
              />
              <ListItemText
                primary={device.productName || device.deviceName}
                secondary={
                  device.supported === false ? (
                    // Shown rather than hidden so an unrecognised printer can be
                    // reported: these identifiers are exactly what is needed to decide
                    // whether it belongs in device_filter.xml.
                    <FormattedMessage
                      id='app.components.app-drawer.printer.usb-unsupported'
                      defaultMessage='Not usable as a printer ({deviceKey}, class {interfaceClasses})'
                      values={{
                        deviceKey: device.deviceKey,
                        interfaceClasses: device.interfaceClasses || '—',
                      }}
                    />
                  ) : device.hasPermission ? (
                    <FormattedMessage
                      id='app.components.app-drawer.printer.usb-ready'
                      defaultMessage='Ready ({deviceKey})'
                      values={{ deviceKey: device.deviceKey }}
                    />
                  ) : (
                    <FormattedMessage
                      id='app.components.app-drawer.printer.usb-tap-to-allow'
                      defaultMessage='Tap to allow access'
                    />
                  )
                }
                primaryTypographyProps={{ sx: { color: PRIMARY_TEXT_COLOR } }}
                secondaryTypographyProps={{ sx: { color: SECONDARY_TEXT_COLOR } }}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Box sx={{ width: '100%', mb: 1 }}>
          <Button
            onClick={() => handleIdentify(usbDevices.some(d => d.hasPermission) ? 'usb' : 'bluetooth')}
            disabled={isIdentifying}
            size='small'
          >
            {isIdentifying ? (
              <FormattedMessage
                id='app.components.app-drawer.printer.identifying'
                defaultMessage='Identifying…'
              />
            ) : (
              <FormattedMessage
                id='app.components.app-drawer.printer.identify'
                defaultMessage='Detect printer model and resolution'
              />
            )}
          </Button>

          {identity && (
            <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.8rem', mt: 0.5 }}>
              {identity.model ?? identity.raw}
              {identity.dpi ? ` · ${identity.dpi} dpi` : ''}
            </Typography>
          )}

          {identifyError && (
            <Typography sx={{ color: '#e2a08a', fontSize: '0.8rem', mt: 0.5 }}>
              {identifyError}
            </Typography>
          )}

          {!identity && !identifyError && getPrinterModel() && (
            <Typography sx={{ color: SECONDARY_TEXT_COLOR, fontSize: '0.8rem', mt: 0.5 }}>
              {getPrinterModel()}
            </Typography>
          )}
        </Box>

        <Button onClick={refresh} disabled={isLoading}>
          <FormattedMessage
            id='app.components.app-drawer.printer.refresh'
            defaultMessage='Refresh'
          />
        </Button>
        <Button onClick={handleClose} variant='contained'>
          <FormattedMessage
            id='app.components.app-drawer.printer.done'
            defaultMessage='Done'
          />
        </Button>
      </DialogActions>
    </Dialog>
  )

  return { selectedBluetoothName, open, dialog }
}
